import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, AlertCircle, AlertTriangle, User, MapPin, Briefcase, ArrowLeft } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import { fechaToInput, fechaToDisplay } from '../utils/fechaUtils';
import { TITULOS_VIGENTES, TITULOS_ANTERIORES } from '../constants/titulos';
import { goBack } from '../utils/navigation';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GENEROS = [
  "Masculino",
  "Femenino", 
  "Feminidad trans/travesti",
  "Varón trans/masculinidad trans",
  "No binarie",
  "Otro"
];

export default function EditarDatosPage() {
  const { trabajador, updateTrabajador, tieneDatosCompletos, checkDatosCompletos, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [datosIncompletos, setDatosIncompletos] = useState(false);

  const [formData, setFormData] = useState({
    numero_matricula: '',
    documento: '',
    apellido: '',
    nombre: '',
    fecha_nacimiento: '',
    genero: '',
    titulo: '',
    email: '',
    celular: '',
    domicilio_real: { calle: '', numero: '', piso: '', depto: '', localidad: '' },
    domicilio_laboral: { calle: '', numero: '', piso: '', depto: '', localidad: '' },
    sin_empleo: false
  });

  useEffect(() => {
    if (trabajador) {
      setFormData({
        numero_matricula: trabajador.numero_matricula || '',
        documento: trabajador.documento || '',
        apellido: trabajador.apellido || '',
        nombre: trabajador.nombre || '',
        fecha_nacimiento: fechaToInput(trabajador.fecha_nacimiento) || '',
        genero: trabajador.genero || '',
        titulo: trabajador.titulo || '',
        email: trabajador.email || '',
        celular: trabajador.celular || '',
        domicilio_real: trabajador.domicilio_real || { calle: '', numero: '', piso: '', depto: '', localidad: '' },
        domicilio_laboral: trabajador.domicilio_laboral || { calle: '', numero: '', piso: '', depto: '', localidad: '' },
        sin_empleo: trabajador.sin_empleo || false
      });
      // Verificar si tiene datos incompletos
      setDatosIncompletos(!checkDatosCompletos(trabajador));
    }
  }, [trabajador, checkDatosCompletos]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateDomicilio = (tipo, field, value) => {
    setFormData(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    // Validar campos obligatorios antes de enviar
    const camposVacios = [];
    if (!formData.fecha_nacimiento?.trim()) camposVacios.push('Fecha de Nacimiento');
    if (!formData.titulo?.trim()) camposVacios.push('Título');
    if (!formData.email?.trim()) camposVacios.push('Email');
    if (!formData.celular?.trim()) camposVacios.push('Celular');
    if (!formData.domicilio_real?.calle?.trim()) camposVacios.push('Domicilio Real - Calle');
    if (!formData.domicilio_real?.numero?.trim()) camposVacios.push('Domicilio Real - Número');
    if (!formData.domicilio_real?.localidad?.trim()) camposVacios.push('Domicilio Real - Localidad');
    // Solo validar domicilio laboral si NO está marcado "sin empleo"
    if (!formData.sin_empleo) {
      if (!formData.domicilio_laboral?.calle?.trim()) camposVacios.push('Domicilio Laboral - Calle');
      if (!formData.domicilio_laboral?.numero?.trim()) camposVacios.push('Domicilio Laboral - Número');
      if (!formData.domicilio_laboral?.localidad?.trim()) camposVacios.push('Domicilio Laboral - Localidad');
    }

    if (camposVacios.length > 0) {
      setError(`Campos obligatorios incompletos: ${camposVacios.join(', ')}`);
      setLoading(false);
      return;
    }

    try {
      // Usar axios directamente con el ID del trabajador
      if (!trabajador?.id) {
        throw new Error('No se encontró el ID del trabajador');
      }
      
      // Obtener token del localStorage explícitamente
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No autenticado - por favor volvé a iniciar sesión');
      }
      
      // Convertir fecha al formato dd/mm/yyyy para guardar
      const dataToSave = {
        ...formData,
        fecha_nacimiento: fechaToDisplay(formData.fecha_nacimiento)
      };
      
      await axios.put(`${API_URL}/trabajadores/${trabajador.id}`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refrescar datos del trabajador
      await updateTrabajador();
      setSuccess(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      console.error('Error guardando datos:', err);
      setError(err.response?.data?.detail || err.message || 'Error al guardar datos');
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loading mientras el AuthContext carga
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  // Si no hay trabajador vinculado, mostrar mensaje de error
  if (!trabajador) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UserHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-amber-800 mb-2">Perfil no encontrado</h2>
            <p className="text-amber-700 mb-4">
              No se encontró un perfil de trabajador social vinculado a tu cuenta.
            </p>
            <p className="text-sm text-amber-600 mb-6">
              Si ya estás matriculado/a, contactá al Colegio para vincular tu cuenta con tu matrícula.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header unificado */}
      <UserHeader />
      
      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {datosIncompletos ? 'Completá tus Datos' : 'Editar Mis Datos'}
            </h1>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Alerta de datos incompletos */}
        {datosIncompletos && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Datos Incompletos</h3>
              <p className="text-sm text-amber-700">
                Tu cuenta fue vinculada pero faltan datos obligatorios. Por favor completá toda la información marcada con * para poder continuar.
              </p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Información Personal</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              {success && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
                  <Save className="w-4 h-4" />
                  Datos guardados correctamente. Redirigiendo...
                </div>
              )}

              {/* Datos Básicos */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Datos Básicos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Número de Matrícula</Label>
                    <Input value={formData.numero_matricula} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Documento</Label>
                    <Input value={formData.documento} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Apellido</Label>
                    <Input value={formData.apellido} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={formData.nombre} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Nacimiento *</Label>
                    <Input type="date" value={formData.fecha_nacimiento} onChange={(e) => updateFormData('fecha_nacimiento', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Género</Label>
                    <select 
                      value={formData.genero} 
                      onChange={(e) => updateFormData('genero', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Seleccionar</option>
                      {GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Título *</Label>
                    <select 
                      value={formData.titulo} 
                      onChange={(e) => updateFormData('titulo', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="">Seleccionar</option>
                      <optgroup label="Titulaciones vigentes">
                        {TITULOS_VIGENTES.map(t => <option key={t} value={t}>{t}</option>)}
                      </optgroup>
                      <optgroup label="Titulaciones anteriores">
                        {TITULOS_ANTERIORES.map(t => <option key={t} value={t}>{t}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} required />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Celular *</Label>
                    <Input value={formData.celular} onChange={(e) => updateFormData('celular', e.target.value)} required />
                  </div>
                </div>
              </div>

              {/* Domicilio Real */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Domicilio Real
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Calle *</Label>
                    <Input value={formData.domicilio_real.calle} onChange={(e) => updateDomicilio('domicilio_real', 'calle', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Número *</Label>
                    <Input value={formData.domicilio_real.numero} onChange={(e) => updateDomicilio('domicilio_real', 'numero', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Piso</Label>
                    <Input value={formData.domicilio_real.piso} onChange={(e) => updateDomicilio('domicilio_real', 'piso', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Depto</Label>
                    <Input value={formData.domicilio_real.depto} onChange={(e) => updateDomicilio('domicilio_real', 'depto', e.target.value)} />
                  </div>
                  <div className="col-span-2 md:col-span-3 space-y-2">
                    <Label>Localidad *</Label>
                    <Input value={formData.domicilio_real.localidad} onChange={(e) => updateDomicilio('domicilio_real', 'localidad', e.target.value)} required />
                  </div>
                </div>
              </div>

              {/* Domicilio Laboral */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Domicilio Laboral
                </h3>
                
                {/* Checkbox sin empleo */}
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.sin_empleo}
                      onChange={(e) => updateFormData('sin_empleo', e.target.checked)}
                      className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                      data-testid="sin-empleo-checkbox"
                    />
                    <span className="text-amber-800 font-medium">Me encuentro actualmente sin empleo</span>
                  </label>
                </div>

                {!formData.sin_empleo && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Calle *</Label>
                      <Input value={formData.domicilio_laboral.calle} onChange={(e) => updateDomicilio('domicilio_laboral', 'calle', e.target.value)} required={!formData.sin_empleo} />
                    </div>
                    <div className="space-y-2">
                      <Label>Número *</Label>
                      <Input value={formData.domicilio_laboral.numero} onChange={(e) => updateDomicilio('domicilio_laboral', 'numero', e.target.value)} required={!formData.sin_empleo} />
                    </div>
                    <div className="space-y-2">
                      <Label>Piso</Label>
                      <Input value={formData.domicilio_laboral.piso} onChange={(e) => updateDomicilio('domicilio_laboral', 'piso', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Depto</Label>
                      <Input value={formData.domicilio_laboral.depto} onChange={(e) => updateDomicilio('domicilio_laboral', 'depto', e.target.value)} />
                    </div>
                    <div className="col-span-2 md:col-span-3 space-y-2">
                      <Label>Localidad *</Label>
                      <Input value={formData.domicilio_laboral.localidad} onChange={(e) => updateDomicilio('domicilio_laboral', 'localidad', e.target.value)} required={!formData.sin_empleo} />
                    </div>
                  </div>
                )}

                {formData.sin_empleo && (
                  <p className="text-sm text-gray-500 italic">Los campos de domicilio laboral no son requeridos cuando no tenés empleo actualmente.</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                {!datosIncompletos && (
                  <Button type="button" variant="outline" onClick={() => goBack()} className="flex-1">
                    Cancelar
                  </Button>
                )}
                <Button type="submit" className={datosIncompletos ? "w-full" : "flex-1"} disabled={loading} data-testid="save-datos-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Guardando...' : 'Guardar Datos'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
